/**
 * ReportGarmentIssueDialog — shared "report a production issue on a garment"
 * dialog used at the catcher, film-print, packing stations and the tracker
 * (report-issue-everywhere). On open it asks the backend which issue types are
 * valid for the scanned/clicked garment's current state and renders ONLY those
 * (no greyed-out dead options). Each type dispatches to its existing cleanup:
 *   missing_stock → pull the whole order from the batch (coordinator-only)
 *   misprint      → discard + reprint (redo)
 *   damaged/wrong_item → PAP damage / wrong-item
 *
 * `context` flips the touch-target scale: 'station' (scanner-first, h-14) vs
 * 'desk' (tracker, h-12).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, Printer, Boxes, PackageX } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { trpc } from '@/lib/trpc';

export type GarmentIssueType = 'missing_stock' | 'misprint' | 'damaged' | 'wrong_item';

interface ReportGarmentIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  garmentUnitId: string | null;
  /** Scanner-first station vs calmer desk (tracker) sizing. */
  context?: 'station' | 'desk';
  /** Optional context shown in the header. */
  productName?: string | null;
  sku?: string | null;
  /** Called after a successful report (parent should refetch / advance). */
  onReported?: () => void;
}

const ISSUE_META: Record<
  GarmentIssueType,
  { labelKey: string; descKey: string; Icon: typeof Printer }
> = {
  missing_stock: { labelKey: 'garmentIssue.missingStock', descKey: 'garmentIssue.missingStockDesc', Icon: PackageX },
  misprint: { labelKey: 'garmentIssue.misprint', descKey: 'garmentIssue.misprintDesc', Icon: Printer },
  damaged: { labelKey: 'garmentIssue.damaged', descKey: 'garmentIssue.damagedDesc', Icon: AlertTriangle },
  wrong_item: { labelKey: 'garmentIssue.wrongItem', descKey: 'garmentIssue.wrongItemDesc', Icon: Boxes },
};

export function ReportGarmentIssueDialog({
  open,
  onOpenChange,
  garmentUnitId,
  context = 'station',
  productName,
  sku,
  onReported,
}: ReportGarmentIssueDialogProps) {
  const { t } = useTranslation('production');
  const utils = trpc.useUtils();
  const [selected, setSelected] = useState<GarmentIssueType | null>(null);
  const [notes, setNotes] = useState('');

  const btnHeight = context === 'station' ? 'h-14' : 'h-12';

  const validQuery = trpc.productionOrders.getValidGarmentIssueTypes.useQuery(
    { garmentUnitId: garmentUnitId ?? '' },
    { enabled: open && !!garmentUnitId, staleTime: 0 }
  );

  const reportMutation = trpc.productionOrders.reportGarmentIssue.useMutation({
    onSuccess: () => {
      toast.success(t('garmentIssue.reported'));
      void utils.productionOrders.invalidate();
      handleOpenChange(false);
      onReported?.();
    },
    onError: (error) => {
      toast.error(getUserMessage(error));
    },
  });

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSelected(null);
      setNotes('');
    }
    onOpenChange(next);
  };

  const handleSubmit = () => {
    if (!garmentUnitId || !selected) return;
    reportMutation.mutate({
      garmentUnitId,
      issueType: selected,
      notes: notes.trim() || undefined,
    });
  };

  const validTypes = validQuery.data?.validIssueTypes ?? [];
  // Drives the status-aware empty message: explain WHY nothing is reportable.
  const orderStatus = validQuery.data?.orderStatus;
  const isPending = reportMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-6 h-6" aria-hidden="true" />
            {t('garmentIssue.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {(productName || sku) && (
            <div className="bg-muted/50 rounded-lg p-3">
              {productName && <p className="font-medium">{productName}</p>}
              {sku && (
                <Badge variant="outline" className="text-xs font-mono mt-1">
                  {sku}
                </Badge>
              )}
            </div>
          )}

          {validQuery.isLoading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : validQuery.isError ? (
            <p className="text-sm text-destructive">{getUserMessage(validQuery.error)}</p>
          ) : validTypes.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {orderStatus === 'ON_HOLD' || orderStatus === 'WAITING_STOCK'
                ? t('garmentIssue.noneReportablePaused')
                : orderStatus === 'CANCELLED'
                  ? t('garmentIssue.noneReportableCancelled')
                  : orderStatus
                    ? t('garmentIssue.noneReportablePastStage')
                    : t('garmentIssue.noneReportable')}
            </p>
          ) : (
            <fieldset className="space-y-2">
              <legend className="text-base font-medium mb-2">{t('garmentIssue.whatsWrong')}</legend>
              <div className="space-y-2" role="radiogroup" aria-required="true">
                {validTypes.map((type) => {
                  const meta = ISSUE_META[type];
                  const isSel = selected === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      role="radio"
                      aria-checked={isSel}
                      onClick={() => setSelected(type)}
                      disabled={isPending}
                      className={cn(
                        'w-full flex items-center gap-4 p-4 rounded-lg border-2 text-left transition-colors',
                        isSel ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20' : 'border-muted hover:border-muted-foreground/30',
                        isPending && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <meta.Icon className={cn('w-6 h-6 flex-shrink-0', isSel ? 'text-amber-600' : 'text-muted-foreground')} aria-hidden="true" />
                      <div className="flex-1">
                        <span className="font-medium text-base">{t(meta.labelKey)}</span>
                        <span className="block text-sm text-muted-foreground">{t(meta.descKey)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}

          {selected === 'missing_stock' && (
            <p className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 p-3 text-sm text-amber-800 dark:text-amber-300">
              <PackageX className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
              {t('garmentIssue.pullWarning')}
            </p>
          )}

          {validTypes.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="garment-issue-notes" className="text-base font-medium">
                {t('garmentIssue.notes')}
              </Label>
              <Textarea
                id="garment-issue-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('garmentIssue.notesPlaceholder')}
                className="h-24 resize-none"
                disabled={isPending}
                maxLength={500}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending} className={btnHeight}>
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selected || isPending || validTypes.length === 0}
            className={cn(btnHeight, 'bg-amber-500 text-white hover:bg-amber-600')}
          >
            {isPending ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" aria-hidden="true" />
                {t('reporting')}
              </>
            ) : (
              t('garmentIssue.submit')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
